import { ArticleResolution } from '@wix/answers-api';
import { Box } from '@wix/design-system';
import { TSupportLanguage } from '@wix/wix-chatbot-common/locale';
import React, { FC, useMemo } from 'react';

import { renderModel } from '../../../util/renderModel';
import { HelpCenterArticle } from '../../types';
import { ResolutionEtaTag } from './ResolutionEtaTag';
import { ResolutionStatusTag } from './ResolutionStatusTag';

export interface FeatureRequestStatusProps {
  article: HelpCenterArticle;
}

const useDecoratedArticle = (article: HelpCenterArticle, locale: TSupportLanguage) => {
  return useMemo(() => {
    if (locale === 'en') {
      return article;
    }

    const articleCopy = { ...article };

    // Avoiding any ETAs for non-english locales until translations are ready.
    delete articleCopy.resolutionEta;

    if (
      articleCopy.resolution &&
      [ArticleResolution.PLANNED, ArticleResolution.IN_DEVELOPMENT, ArticleResolution.COMING_SOON].includes(
        articleCopy.resolution,
      )
    ) {
      // Using "collection votes" resolution for all non-english locales in case feature request is not completed yet.
      articleCopy.resolution = ArticleResolution.COLLECTING_VOTES;
    }

    return articleCopy;
  }, [article, locale]);
};

export const FeatureRequestStatus: FC<FeatureRequestStatusProps> = ({ article }) => {
  const locale = renderModel.getLocale();
  const decoratedArticle = useDecoratedArticle(article, locale);

  return (
    <Box gap="SP1">
      <ResolutionStatusTag article={decoratedArticle} />
      <ResolutionEtaTag article={decoratedArticle} />
    </Box>
  );
};
